/* KANON `Raporty` — the roster on a survey card, transcribed from `01-ankiety.html`'s `.ava`:
 *   disc     34px, a circle, 2px ring in the card's own ground, 12 / 700
 *   overlap  6px, front to back, so the first face is on top
 *   +N       the same disc at 800 and tabular figures, LAST in the stack
 *
 * ── The disc is a NEUTRAL STEP above the card, not a white one ───────────────────────────────
 * `--bg-chip` over `--text`, with the ring in the card's own `--bg-card`. The canon's own discs are
 * `#2e2e33` with white initials on a `#161618` card — a step above the ground, not a reversed chip —
 * and reading them as "white disc, dark ink" is what `--chip-solid-fill` would have given. Measured
 * in Flame at 1440x900: a white disc, a near-white ring and a white card drew four initials floating
 * on nothing, which is the same failure the plan library's strip records as owner decision D20. The
 * step keeps the disc legible in both themes and the ring is then the canon's own colour.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js). */

.stack {
  display: inline-flex;
  flex: none;
  align-items: center;
}

.face {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-report-face);
  height: var(--sz-report-face);
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  letter-spacing: var(--ls-nav);
  color: var(--text);
  background: var(--bg-chip);

  /* Two pixels, which is what the canon writes. `--border-focus` is the app's only 2px width token;
   * it is a WIDTH here and nothing to do with focus. */
  border: var(--border-focus) solid var(--bg-card);
  border-radius: var(--r-circle);
}

/* Every disc but the first sits 6px over the one before it, and the z-order descends so the first
 * face is on top. Without it the last disc would overlap the ones before it and the stack would
 * read backwards. */
.face + .face {
  margin-inline-start: -6px;
}

/* The remainder. It is a FIGURE about the list, not another person, so it is set apart from the
 * faces by ink and weight — the canon's own `#b6b6bd` on the same disc — rather than by a second
 * fill. Giving it a person's ink is what makes a „+8" read as a client whose initials are plus and
 * eight. Tabular, because it is a number. */
.more {
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* ── The EDITOR cut, and it is the MOBILE app's rather than the web export's ──────────────────
 * The owner compared the editor's assigned bar with the phone's on 17 September 2026 and asked for
 * the phone's — „jak w apce mobilnej". So this cut transcribes
 * `PersonAvatar.specs.ts`'s `checkInStack` role and `ClientAvatarStack` around it: 32px discs at
 * 11.5/800, overlapping by 9, up to five of them. It was the export's 26px at 10px type, which is a
 * size a photograph cannot be read at and which the owner refused.
 *
 * Everything this cut does NOT change stays above — the ground, the 2px ring in the card colour,
 * the ink and the one accessible name over the strip. `--sz-avatar` is not read here: this disc is
 * `AvatarStack`'s own, not `AvatarChip`'s. */
.editor .face {
  width: var(--sz-report-face-editor);
  height: var(--sz-report-face-editor);
  font-size: var(--fs-micro);
}

/* Nine, the phone's `AVATAR_OVERLAP`. A negative inline start margin is not one of the properties
 * stylelint's value allowlist covers (see `stylelint.config.js` — `margin-inline-start` is absent
 * from it), and the two overlaps above are written the same way: it is the stack's own geometry
 * rather than a spacing decision, and a token per overlap would be three tokens read once each. */
.editor .face + .face {
  margin-inline-start: -9px;
}

/* The phone draws the „+N" chip on `bgInset` rather than on the disc's own ground — it is a count
 * standing at the end of a row of people, and the step down is what says so. */
.editor .more {
  background: var(--bg-inset);
}
